import { useMemo } from "react";
import Carousel from "react-material-ui-carousel";
import { Box } from "@mui/material";
import { useBannerConfig } from "../../../Common/hooks/useBannerConfig";
import {
  mapCategoryToSeverity,
  mapVariant,
} from "../../../Common/utils/bannerMapping";
import { computeTextHash } from "../../../Common/utils/hashUtils";
import AlertBanner from "./AlertBanner";
import { SettingsKeys } from "../../../Common/models/storageSettingsKeys";
import { getItem } from "../../../Common/utils/storageUtil";

const RotatingAlertBanners = (): JSX.Element | null => {
  const { banners } = useBannerConfig();

  const activeBanners = useMemo(() => {
    if (!banners) return [];
    return banners.filter((b) => {
      if (!b.isActive) return false;
      // Persistent dismissal state stored under banner:<id>
      const persisted = getItem(localStorage, `${SettingsKeys.Banner}:${b.id}`);
      if (!persisted) return true; // Not dismissed, show it

      // Compute current text hash
      const currentHash = computeTextHash(b.text);

      // If text changed (hash mismatch), re-show the banner even if previously dismissed
      if (persisted.lastTextHash && persisted.lastTextHash !== currentHash) {
        return true;
      }

      // Otherwise, respect the dismissed flag
      return !persisted.dismissed;
    });
  }, [banners]);

  if (!activeBanners.length) return null;

  return (
    <Box sx={{ width: "100%" }} data-testid="rotating-alert-banners">
      <Carousel
        autoPlay={true}
        animation="slide"
        interval={8000}
        cycleNavigation={true}
        indicators={false}
        navButtonsAlwaysInvisible={true}
        swipe={activeBanners.length > 1}
      >
        {activeBanners.map((b) => {
          return (
            <AlertBanner
              key={b.id}
              id={b.id}
              text={b.text}
              severity={mapCategoryToSeverity(b.category)}
              variant={mapVariant(b.variant)}
              isActive={b.isActive}
              customBackgroundColor={b.customBackgroundColor}
              previewMode={false}
            />
          );
        })}
      </Carousel>
    </Box>
  );
};

export default RotatingAlertBanners;
